// ── QuickExpenseEntry — 빠른 비용 입력 모달 ────────────────────
// 5개 카테고리 (식재료/인건비/임대료/공과금/기타) + 거래처 + 메모
// 거래장부에 expense transaction(들)으로 저장
import React, { useState, useEffect } from 'react';
import { L, M, MU } from '../i18n/L';   // V160 중국어

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

const EXPENSE_TYPES = [
  { id: 'material',  get label() { return L("식재료"); },  icon: '🥬', color: '#18181B',
    subId: 'raw_material', get placeholder() { return L("식재료비 합계"); } },
  { id: 'labor',     get label() { return L("인건비"); },  icon: '👥', color: '#A1A1AA',
    subId: 'salary',       get placeholder() { return L("직원 급여·일당"); } },
  { id: 'operation', get label() { return L("임대료"); },  icon: '🏪', color: '#3b82f6',
    subId: 'rent',         get placeholder() { return L("월세·관리비"); } },
  { id: 'operation', get label() { return L("공과금"); },  icon: '⚡', color: '#8b5cf6',
    subId: 'utilities',    get placeholder() { return L("전기·수도·가스"); } },
  { id: 'etc',       get label() { return L("기타"); },    icon: '📋', color: '#6b7280',
    subId: 'misc',         get placeholder() { return L("기타 잡비"); } },
];

export default function QuickExpenseEntry({
  isOpen,
  defaultDate,        // 'YYYY-MM-DD' or null
  onClose,
  onSave,             // async (transactions[]) => void
  isMobile,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(defaultDate || today);
  const [amounts, setAmounts] = useState(EXPENSE_TYPES.reduce((o, t, i) => ({ ...o, [i]: 0 }), {}));
  const [partner, setPartner] = useState('');
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 모달 열릴 때 reset
  useEffect(() => {
    if (isOpen) {
      setDate(defaultDate || today);
      setAmounts(EXPENSE_TYPES.reduce((o, t, i) => ({ ...o, [i]: 0 }), {}));
      setPartner('');
      setMemo('');
      setError('');
    }
  }, [isOpen, defaultDate]);

  if (!isOpen) return null;

  const total = Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0);

  const handleSave = async () => {
    if (total <= 0) {
      setError(L("비용 금액을 입력해주세요"));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const txs = [];
      EXPENSE_TYPES.forEach((t, i) => {
        const amt = Number(amounts[i]) || 0;
        if (amt <= 0) return;
        // VAT 포함 가정 (총액 = supply + vat). 일반 음식점 비용
        const supply = Math.round(amt / 1.1);
        const vat = amt - supply;
        txs.push({
          type: 'expense',
          date,
          supply,
          vat,
          total: amt,
          partnerId: null,
          partnerName: partner || '',
          evidence: { type: 'card', isQualified: true, sourceUrl: null, externalId: null },
          category: { major: t.id, sub: t.subId },
          revenueCategory: null,
          itemName: '',
          memo: memo || `${t.label} (빠른 입력)`,
          vatTreatment: { deductible: true, presumed: false, presumedRate: null },
        });
      });
      await onSave(txs);
      onClose();
    } catch (e) {
      setError(e.message || L("저장 실패"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
      padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff',
        borderRadius: 16,
        padding: isMobile ? '20px 18px' : '24px 26px',
        maxWidth: 480,
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 12px 60px rgba(0,0,0,0.3)',
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 14,
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a1a' }}>
              {L("💸 빠른 비용 입력")}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              {L("거래장부에 비용 transaction으로 저장")}
            </div>
          </div>
          <button onClick={onClose} aria-label={L("닫기")} style={{
            width: 30, height: 30, borderRadius: '50%',
            border: '1px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 16,
            cursor: 'pointer',
          }}>×</button>
        </div>

        {/* 날짜 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 5 }}>
            {L("📅 비용 날짜")}
          </label>
          <input type="date" value={date} max={today}
            onChange={e => setDate(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 8,
              fontSize: 14, outline: 'none',
            }}
          />
        </div>

        {/* 거래처 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 5 }}>
            {L("🏢 거래처 (선택)")}
          </label>
          <input type="text" value={partner} onChange={e => setPartner(e.target.value)}
            placeholder={L("예: 한국야쿠르트, 한전, 강남빌딩")}
            style={{
              width: '100%', padding: '9px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 8,
              fontSize: 13, outline: 'none',
            }}
          />
        </div>

        {/* 비용 분류 입력 */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 6 }}>
            {L("💰 비용 분류 (입력 안 한 항목은 0원)")}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {EXPENSE_TYPES.map((t, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px',
                background: amounts[i] > 0 ? `${t.color}10` : '#fafbfc',
                borderRadius: 7,
                border: `1.5px solid ${amounts[i] > 0 ? t.color : '#e5e7eb'}`,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{t.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: t.color, width: 56, flexShrink: 0 }}>
                  {t.label}
                </span>
                <input type="number" min="0"
                  value={amounts[i] || ''}
                  placeholder={t.placeholder}
                  onChange={e => setAmounts({ ...amounts, [i]: Number(e.target.value) || 0 })}
                  style={{
                    flex: 1, minWidth: 0,
                    padding: '6px 10px',
                    border: '1px solid #e5e7eb', borderRadius: 6,
                    fontSize: 13, textAlign: 'right',
                    outline: 'none', background: '#fff',
                  }}
                />
                <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>{MU()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 합계 */}
        <div style={{
          padding: '10px 14px',
          background: total > 0 ? '#FAFAFA' : '#f3f4f6',
          borderRadius: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: total > 0 ? '#EA580C' : '#9ca3af' }}>
            {L("이날 비용 합계")}
          </span>
          <span style={{ fontSize: 18, fontWeight: 900, color: total > 0 ? '#EA580C' : '#9ca3af' }}>
            {M(total)}
          </span>
        </div>

        {/* 메모 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 5 }}>
            {L("📝 메모 (선택)")}
          </label>
          <input type="text"
            value={memo} onChange={e => setMemo(e.target.value)}
            placeholder={L("예: 야채·고기 매입 / 5월 임대료")}
            style={{
              width: '100%', padding: '9px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 8,
              fontSize: 13, outline: 'none',
            }}
          />
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', background: '#F4F4F5',
            color: '#52525B', borderRadius: 8,
            fontSize: 12, marginBottom: 10,
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* 안내 */}
        <div style={{
          padding: '8px 12px', background: '#f0f9ff',
          borderRadius: 8, fontSize: 11, color: '#0369a1',
          lineHeight: 1.5, marginBottom: 12,
        }}>
          {L("💡 부가세 포함으로 저장 (공급가액 = 입력값 ÷ 1.1, 부가세 = 10%). 카테고리·증빙 종류는 거래장부에서 수정 가능")}
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{
            flex: 1, padding: '11px',
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 13, fontWeight: 700,
            borderRadius: 9, cursor: 'pointer',
          }}>{L("취소")}</button>
          <button onClick={handleSave} disabled={saving || total <= 0} style={{
            flex: 2, padding: '11px',
            border: 'none',
            background: total > 0 && !saving ? '#EA580C' : '#9ca3af',
            color: '#fff', fontSize: 13, fontWeight: 800,
            borderRadius: 9, cursor: total > 0 && !saving ? 'pointer' : 'not-allowed',
          }}>
            {saving ? L("⏳ 저장 중...") : L("✅ {0} 저장", M(total))}
          </button>
        </div>
      </div>
    </div>
  );
}
